import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Snackbar,
    Switch,
    FormControlLabel,
    Card,
    CardContent,
    CardHeader,
    Breadcrumbs,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tab,
    Tabs,
    Grid,
    ListItemButton,
    ListItemIcon,
    Divider,
    Paper,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Policy as PolicyIcon, Schedule as ScheduleIcon, AccessTime as AccessTimeIcon, Timer as TimerIcon } from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoneyIcon from '@mui/icons-material/Money';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { tenantSettingsAPI, membershipTierAPI, personalTrainingTypeAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { TenantSettings, MembershipTier } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import DropdownManagement from './DropdownManagement';
import BookingWindowConfigurationComponent from '../common/BookingWindowConfiguration';
import { DocumentReviewSettingsPanel } from './DocumentReviewSettingsPanel';
import { DocumentTemplateManagement } from './DocumentTemplateManagement';

// TabPanel component to manage tabs
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
            style={{ padding: '20px 0' }}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

const SettingsPage: React.FC = () => {
    const { user } = useAuth();
    const [settings, setSettings] = useState<TenantSettings>({
        email_notifications_for_new_applications: true,
        admin_email_list: [],
        tenant_heading_title: '',
        tenant_heading_subtitle: '',
        tenant_logo_img: '',
        tenant_hero_img: '',
        tenant_background_colour: '#ffffff',
        cancelWithoutPenaltyHrs: 24,
        default_booking_windows: {},
        guest_access_limit: 4,
        guest_fee: 0,
        guest_sponsorship_limit: 4,
        guest_pass_validity_days: 7,
        on_hold_fees: {},
        bookingOpenTime: '00:01',
        daily_amenity_booking_limit: 120,
        prime_time_booking_limit: 0,
        receipt_organization_name: '',
        receipt_organization_address: '',
        receipt_gst_hst_number: ''
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [newEmail, setNewEmail] = useState<string>('');
    const [tabIndex, setTabIndex] = useState(0);
    const [showVersion, setShowVersion] = useState(false);
    const [systemTabSelection, setSystemTabSelection] = useState<string>('cancellation-policy');
    const [ptTypes, setPTTypes] = useState<any[]>([]);
    const [ptTypeDialogOpen, setPTTypeDialogOpen] = useState(false);
    const [editingPTType, setEditingPTType] = useState<any>(null);
    const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);

    // Check if user has permission to edit settings
    const canEditSettings = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

    // Fetch settings
    useEffect(() => {
        fetchSettings();
        fetchMembershipTiers();
        fetchPTTypes();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await tenantSettingsAPI.getAllSettings();
            setSettings(response.data);
        } catch (err: any) {
            console.error('Error fetching settings:', err);
            setError(err.response?.data?.message || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembershipTiers = async () => {
        try {
            const response = await membershipTierAPI.getMembershipTiers(true);
            setMembershipTiers(response.data);
        } catch (err: any) {
            console.error('Error fetching membership tiers:', err);
        }
    };

    // Save all settings
    const saveSettings = async () => {
        setSaving(true);
        setError(null);
        try {
            await tenantSettingsAPI.updateMultipleSettings(settings);
            setSuccessMessage('Settings updated successfully');
        } catch (err: any) {
            console.error('Error saving settings:', err);
            setError(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    // Update a single setting
    const updateSetting = async (key: keyof TenantSettings, value: any) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    // Update on-hold fee for a specific membership tier
    const updateOnHoldFee = (tierId: string, fee: number) => {
        const updatedFees = { ...(settings.on_hold_fees || {}), [tierId]: fee };
        updateSetting('on_hold_fees', updatedFees);
    };

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    // Add email to admin_email_list
    const addEmail = () => {
        if (newEmail.trim() && isValidEmail(newEmail)) {
            if (!settings.admin_email_list.includes(newEmail)) {
                const updatedEmails = [...settings.admin_email_list, newEmail];
                updateSetting('admin_email_list', updatedEmails);
            }
            setNewEmail('');
        }
    };

    // Remove email from admin_email_list
    const removeEmail = (email: string) => {
        const updatedEmails = settings.admin_email_list.filter(e => e !== email);
        updateSetting('admin_email_list', updatedEmails);
    };

    // Validate email format
    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    // Fetch PT Types
    const fetchPTTypes = async () => {
        try {
            const response = await personalTrainingTypeAPI.getAll();
            setPTTypes(response.data);
        } catch (err: any) {
            console.error('Error fetching PT types:', err);
        }
    };

    // Save PT Type
    const savePTType = async (data: { title: string; defaultPrice: number }) => {
        try {
            if (editingPTType) {
                await personalTrainingTypeAPI.update(editingPTType.id, data);
                setSuccessMessage('PT type updated successfully');
            } else {
                await personalTrainingTypeAPI.create(data);
                setSuccessMessage('PT type created successfully');
            }
            await fetchPTTypes();
            setPTTypeDialogOpen(false);
            setEditingPTType(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save PT type');
        }
    };

    // Show color preview
    const colorPreview = (color: string) => {
        return (
            <Box
                sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    display: 'inline-block',
                    marginLeft: 1
                }}
            />
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <AdminAccessControl>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <RouterLink
                        className="user-link"
                        to="/dashboard"
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Home
                    </RouterLink>
                    <Typography
                        sx={{ display: 'flex', alignItems: 'center' }}
                        color="text.primary"
                    >
                        <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Settings
                    </Typography>
                </Breadcrumbs>

                {/* Page title */}
                <Typography variant="h4" gutterBottom component="h1">
                    Tenant Settings
                </Typography>

                {/* Error and success messages */}
                {error && (
                    <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
                )}
                <Snackbar
                    open={!!successMessage}
                    autoHideDuration={6000}
                    onClose={() => setSuccessMessage(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSuccessMessage(null)} severity="success">
                        {successMessage}
                    </Alert>
                </Snackbar>

                {/* Settings tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={tabIndex} onChange={handleTabChange} aria-label="settings tabs">
                        <Tab label="General" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
                        <Tab label="Notifications" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
                        <Tab label="Appearance" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
                        <Tab label="System" id="settings-tab-3" aria-controls="settings-tabpanel-3" />
                        <Tab label="Receipts" id="settings-tab-4" aria-controls="settings-tabpanel-4" />
                    </Tabs>
                </Box>
                {/* General Settings Tab */}
                <TabPanel value={tabIndex} index={0}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card sx={{ width: '100%' }}>
                                    <CardHeader title="General Settings" />
                                    <CardContent>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Tenant Heading Title"
                                            value={settings.tenant_heading_title}
                                            onChange={(e) => updateSetting('tenant_heading_title', e.target.value)}
                                            disabled={!canEditSettings}
                                            helperText="This title appears in the header of your tenant's pages"
                                        />

                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Tenant Heading Subtitle"
                                            value={settings.tenant_heading_subtitle}
                                            onChange={(e) => updateSetting('tenant_heading_subtitle', e.target.value)}
                                            disabled={!canEditSettings}
                                            helperText="This subtitle appears below the title in the header"
                                        />

                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </TabPanel>

                {/* Notifications Settings Tab */}
                <TabPanel value={tabIndex} index={1}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box component={Card} width="100%">
                                <CardHeader title="Email Notifications" />
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.email_notifications_for_new_applications}
                                                onChange={(e) => updateSetting('email_notifications_for_new_applications', e.target.checked)}
                                                disabled={!canEditSettings}
                                            />
                                        }
                                        label="Email notifications for new applications"
                                    />

                                    <Typography variant="subtitle1" gutterBottom component="div" sx={{ mt: 2 }}>
                                        Admin Email List
                                    </Typography>

                                    <Box sx={{ display: 'flex', mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Add Admin Email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            disabled={!canEditSettings}
                                            error={!!newEmail && !isValidEmail(newEmail)}
                                            helperText={newEmail && !isValidEmail(newEmail) ? 'Invalid email format' : ''}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={addEmail}
                                                            disabled={!canEditSettings || !newEmail || !isValidEmail(newEmail)}
                                                            edge="end"
                                                        >
                                                            <AddCircleIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    <List dense>
                                        {settings.admin_email_list.map((email, index) => (
                                            <ListItem
                                                key={index}
                                                secondaryAction={
                                                    canEditSettings && (
                                                        <IconButton edge="end" aria-label="delete" onClick={() => removeEmail(email)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )
                                                }
                                            >
                                                <ListItemText primary={email} />
                                            </ListItem>
                                        ))}
                                    </List>

                                    {settings.admin_email_list.length === 0 && (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            No admin emails added yet
                                        </Alert>
                                    )}
                                </CardContent>
                            </Box>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* System Settings Tab */}
                <TabPanel value={tabIndex} index={3}>
                    <Grid container spacing={3}>
                        {/* Left Navigation Panel */}
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    System Settings
                                </Typography>
                                <List component="nav" disablePadding>
                                    <ListItemButton
                                        selected={systemTabSelection === 'cancellation-policy'}
                                        onClick={() => setSystemTabSelection('cancellation-policy')}
                                    >
                                        <ListItemIcon>
                                            <PolicyIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Class Cancellation Policy" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'booking-windows'}
                                        onClick={() => setSystemTabSelection('booking-windows')}
                                    >
                                        <ListItemIcon>
                                            <ScheduleIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Default Booking Windows" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'dropdown-management'}
                                        onClick={() => setSystemTabSelection('dropdown-management')}
                                    >
                                        <ListItemIcon>
                                            <ListAltIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Dropdown Options" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'document-review'}
                                        onClick={() => setSystemTabSelection('document-review')}
                                    >
                                        <ListItemIcon>
                                            <DescriptionIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Document Review" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'guest-passes'}
                                        onClick={() => setSystemTabSelection('guest-passes')}
                                    >
                                        <ListItemIcon>
                                            <CardGiftcardIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Guest Pass Settings" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'on-hold-fees'}
                                        onClick={() => setSystemTabSelection('on-hold-fees')}
                                    >
                                        <ListItemIcon>
                                            <MoneyIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="On Hold Fees" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'pt-types'}
                                        onClick={() => setSystemTabSelection('pt-types')}
                                    >
                                        <ListItemIcon>
                                            <FitnessCenterIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Personal Training Types" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'booking-open-time'}
                                        onClick={() => setSystemTabSelection('booking-open-time')}
                                    >
                                        <ListItemIcon>
                                            <AccessTimeIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Booking Open Time" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'daily-booking-limit'}
                                        onClick={() => setSystemTabSelection('daily-booking-limit')}
                                    >
                                        <ListItemIcon>
                                            <TimerIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Daily Booking Limit" />
                                    </ListItemButton>

                                    <ListItemButton
                                        selected={systemTabSelection === 'prime-time-booking-limit'}
                                        onClick={() => setSystemTabSelection('prime-time-booking-limit')}
                                    >
                                        <ListItemIcon>
                                            <TimerIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Prime Time Limit" />
                                    </ListItemButton>
                                </List>
                            </Paper>
                        </Grid>

                        {/* Right Content Panel */}
                        <Grid size={{ xs: 12, md: 9 }}>
                            <Paper sx={{ p: 3, minHeight: 400 }}>
                                {/* Class Cancellation Policy */}
                                {systemTabSelection === 'cancellation-policy' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Class Cancellation Policy
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Configure the cancellation policy for classes.
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            label="Cancellation Window (Hours)"
                                            type="number"
                                            value={settings.cancelWithoutPenaltyHrs}
                                            onChange={(e) => updateSetting('cancelWithoutPenaltyHrs', parseInt(e.target.value) || 24)}
                                            disabled={!canEditSettings}
                                            helperText="Number of hours before class start time that users can cancel without penalty"
                                            InputProps={{
                                                inputProps: {
                                                    min: 0,
                                                    max: 168 // Max 1 week
                                                }
                                            }}
                                            sx={{ maxWidth: 400 }}
                                        />
                                    </Box>
                                )}

                                {/* Default Booking Windows */}
                                {systemTabSelection === 'booking-windows' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Default Booking Windows
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Configure default booking windows for membership tiers. These defaults will be used when creating new classes or amenities,
                                            reducing the need to configure booking windows for each entity individually.
                                        </Typography>
                                        {canEditSettings ? (
                                            <BookingWindowConfigurationComponent
                                                title="Default Booking Windows"
                                                bookingWindows={settings.default_booking_windows}
                                                onBookingWindowsChange={(bookingWindows) => updateSetting('default_booking_windows', bookingWindows)}
                                            />
                                        ) : (
                                            <Alert severity="info">
                                                You need admin privileges to configure default booking windows.
                                            </Alert>
                                        )}
                                    </Box>
                                )}

                                {/* Dropdown Management */}
                                {systemTabSelection === 'dropdown-management' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Dropdown Options Management
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Manage the dropdown options for classes and amenities. You can delete options that are no longer needed,
                                            but only if they're not currently being used.
                                        </Typography>
                                        {canEditSettings ? (
                                            <DropdownManagement />
                                        ) : (
                                            <Alert severity="info">
                                                You need admin privileges to manage dropdown options.
                                            </Alert>
                                        )}
                                    </Box>
                                )}

                                {/* Document Review */}
                                {systemTabSelection === 'document-review' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Document Review Settings
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Configure document review settings and manage document templates that users must review and accept.
                                        </Typography>

                                        {/* Document Review Settings */}
                                        <Box sx={{ mb: 4 }}>
                                            <DocumentReviewSettingsPanel />
                                        </Box>

                                        <Divider sx={{ my: 3 }} />

                                        {/* Document Templates */}
                                        <Box>

                                            <DocumentTemplateManagement />
                                        </Box>
                                    </Box>
                                )}

                                {/* Guest Pass Settings */}
                                {systemTabSelection === 'guest-passes' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Guest Pass Settings
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Configure guest pass policies and fees. These settings control how members can sponsor guests and the limits on guest access.
                                        </Typography>

                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Guest Access Limit (30 days)"
                                                    type="number"
                                                    value={settings.guest_access_limit}
                                                    onChange={(e) => updateSetting('guest_access_limit', parseInt(e.target.value) || 4)}
                                                    disabled={!canEditSettings}
                                                    helperText="Maximum number of times a specific guest can access the club in the last 30 days"
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 1,
                                                            max: 100
                                                        }
                                                    }}
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Guest Fee ($)"
                                                    type="number"
                                                    value={settings.guest_fee}
                                                    onChange={(e) => updateSetting('guest_fee', parseFloat(e.target.value) || 0)}
                                                    disabled={!canEditSettings}
                                                    helperText="Fee charged to member when they bring in a guest"
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 0,
                                                            step: 0.01
                                                        }
                                                    }}
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Member Sponsorship Limit (30 days)"
                                                    type="number"
                                                    value={settings.guest_sponsorship_limit}
                                                    onChange={(e) => updateSetting('guest_sponsorship_limit', parseInt(e.target.value) || 4)}
                                                    disabled={!canEditSettings}
                                                    helperText="Maximum number of guests a member can sponsor in the last 30 days"
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 1,
                                                            max: 100
                                                        }
                                                    }}
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="Guest Pass Validity (days)"
                                                    type="number"
                                                    value={settings.guest_pass_validity_days}
                                                    onChange={(e) => updateSetting('guest_pass_validity_days', parseInt(e.target.value) || 7)}
                                                    disabled={!canEditSettings}
                                                    helperText="Number of days a guest pass remains valid from issue date"
                                                    InputProps={{
                                                        inputProps: {
                                                            min: 1,
                                                            max: 365
                                                        }
                                                    }}
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: 3 }} />

                                        <Alert severity="info">
                                            <Typography variant="body2">
                                                <strong>How Guest Passes Work:</strong>
                                            </Typography>
                                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                                <li>Members can generate guest passes from their profile</li>
                                                <li>Admins/staff can generate guest passes on behalf of members</li>
                                                <li>Guest fee is charged when the pass is generated (pending payment)</li>
                                                <li>Each pass includes a unique 6-character code and QR code</li>
                                                <li>Passes expire after the validity period or when redeemed</li>
                                                <li>Guest access is tracked by email address</li>
                                            </ul>
                                        </Alert>
                                    </Box>
                                )}

                                {/* On Hold Fees */}
                                {systemTabSelection === 'on-hold-fees' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            On Hold Fees
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Configure the monthly fees charged to members when their accounts are on hold.
                                            Select a membership tier and set its on-hold fee.
                                        </Typography>

                                        {membershipTiers.length === 0 ? (
                                            <Alert severity="info">
                                                No membership tiers found. Please create membership tiers first.
                                            </Alert>
                                        ) : (
                                            <Box>
                                                {/* Display configured on-hold fees */}
                                                {settings.on_hold_fees && Object.keys(settings.on_hold_fees).length > 0 && (
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Configured On Hold Fees:
                                                        </Typography>
                                                        <List dense>
                                                            {Object.entries(settings.on_hold_fees).map(([tierId, fee]) => {
                                                                const tier = membershipTiers.find(t => t.id === tierId);
                                                                if (!tier) return null;
                                                                return (
                                                                    <ListItem
                                                                        key={tierId}
                                                                        secondaryAction={
                                                                            canEditSettings && (
                                                                                <IconButton
                                                                                    edge="end"
                                                                                    aria-label="delete"
                                                                                    onClick={() => {
                                                                                        const updatedFees = { ...(settings.on_hold_fees || {}) };
                                                                                        delete updatedFees[tierId];
                                                                                        updateSetting('on_hold_fees', updatedFees);
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon />
                                                                                </IconButton>
                                                                            )
                                                                        }
                                                                    >
                                                                        <ListItemText
                                                                            primary={tier.name}
                                                                            secondary={`$${Number(fee).toFixed(2)} per month`}
                                                                        />
                                                                    </ListItem>
                                                                );
                                                            })}
                                                        </List>
                                                        <Divider sx={{ my: 2 }} />
                                                    </Box>
                                                )}

                                                {/* Add new on-hold fee */}
                                                {canEditSettings && (
                                                    <Box>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            Add/Update On Hold Fee:
                                                        </Typography>
                                                        <Grid container spacing={2} alignItems="flex-start">
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <TextField
                                                                    select
                                                                    fullWidth
                                                                    label="Select Membership Tier"
                                                                    SelectProps={{
                                                                        native: true,
                                                                    }}
                                                                    helperText="Choose a membership tier to set its on-hold fee"
                                                                    defaultValue=""
                                                                    id="tier-select"
                                                                >
                                                                    {membershipTiers
                                                                        .sort((a, b) => a.displayOrder - b.displayOrder)
                                                                        .map((tier) => (
                                                                            <option key={tier.id} value={tier.id}>
                                                                                {tier.name}
                                                                            </option>
                                                                        ))
                                                                    }
                                                                </TextField>
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 4 }}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Monthly Fee"
                                                                    type="number"
                                                                    id="fee-input"
                                                                    defaultValue={0}
                                                                    helperText="Fee to charge per month"
                                                                    InputProps={{
                                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                                        inputProps: {
                                                                            min: 0,
                                                                            step: 0.01
                                                                        }
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid size={{ xs: 12, md: 2 }}>
                                                                <Button
                                                                    fullWidth
                                                                    variant="contained"
                                                                    onClick={() => {
                                                                        const tierSelect = document.getElementById('tier-select') as HTMLSelectElement;
                                                                        const feeInput = document.getElementById('fee-input') as HTMLInputElement;

                                                                        const tierId = tierSelect?.value;
                                                                        const fee = parseFloat(feeInput?.value || '0');

                                                                        if (tierId && tierId !== '') {
                                                                            updateOnHoldFee(tierId, fee);
                                                                            // Reset inputs
                                                                            tierSelect.value = '';
                                                                            feeInput.value = '0';
                                                                        }
                                                                    }}
                                                                    sx={{ height: 56 }}
                                                                >
                                                                    Set Fee
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <Divider sx={{ my: 3 }} />

                                        <Alert severity="info">
                                            <Typography variant="body2">
                                                <strong>About On Hold Fees:</strong>
                                            </Typography>
                                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                                <li>On hold fees are charged monthly while a member's account is in hold status</li>
                                                <li>Members on hold retain their membership tier but have restricted access</li>
                                                <li>Set the fee to $0 if you don't want to charge members during hold periods</li>
                                                <li>These fees are separate from regular membership dues</li>
                                                <li>You can update existing fees by selecting the tier again and setting a new amount</li>
                                            </ul>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Personal Training Types */}
                                {systemTabSelection === 'pt-types' && (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    Personal Training Types
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Define personal training session types with default pricing. These types can be used when scheduling PT sessions.
                                                </Typography>
                                            </Box>
                                            {canEditSettings && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddCircleIcon />}
                                                    onClick={() => {
                                                        setEditingPTType(null);
                                                        setPTTypeDialogOpen(true);
                                                    }}
                                                >
                                                    Add Type
                                                </Button>
                                            )}
                                        </Box>

                                        {ptTypes.length === 0 ? (
                                            <Alert severity="info">
                                                No personal training types configured yet. Click "Add Type" to create your first PT type.
                                            </Alert>
                                        ) : (
                                            <List>
                                                {ptTypes.map((type) => (
                                                    <ListItem
                                                        key={type.id}
                                                        secondaryAction={
                                                            canEditSettings && (
                                                                <Box>
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setEditingPTType(type);
                                                                            setPTTypeDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="delete"
                                                                        onClick={async () => {
                                                                            if (window.confirm(`Delete PT type "${type.title}"?`)) {
                                                                                try {
                                                                                    await personalTrainingTypeAPI.delete(type.id);
                                                                                    await fetchPTTypes();
                                                                                    setSuccessMessage('PT type deleted successfully');
                                                                                } catch (err: any) {
                                                                                    setError(err.response?.data?.message || 'Failed to delete PT type');
                                                                                }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Box>
                                                            )
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={type.title}
                                                            secondary={`Default Price: $${Number(type.defaultPrice).toFixed(2)}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                )}

                                {/* Booking Open Time */}
                                {systemTabSelection === 'booking-open-time' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Booking Open Time
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Set the time when the booking window resets each day. Members can book up to X days in advance from this time.
                                            For example, if set to 7:00 AM and a member has 5-day advance booking, at 7:00 AM on December 8th,
                                            they can book any time slot on December 13th.
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Booking Open Time"
                                            type="time"
                                            value={settings.bookingOpenTime || '00:01'}
                                            onChange={(e) => updateSetting('bookingOpenTime', e.target.value)}
                                            disabled={!canEditSettings}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                step: 60, // 1 minute steps
                                            }}
                                            helperText="Time when booking windows reset daily (24-hour format)"
                                            sx={{ maxWidth: 300 }}
                                        />

                                        <Alert severity="info" sx={{ mt: 3 }}>
                                            <Typography variant="body2">
                                                <strong>How it works:</strong>
                                            </Typography>
                                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                                <li>Every day at the specified time, the booking window advances by one day</li>
                                                <li>Members can book any available time slot on days within their advance booking limit</li>
                                                <li>This applies to amenities, classes, and group play bookings</li>
                                                <li>Default is 12:01 AM (00:01) for daily midnight reset</li>
                                            </ul>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Daily Booking Limit */}
                                {systemTabSelection === 'daily-booking-limit' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Daily Booking Limit
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Set the maximum number of minutes a member can book per amenity type per day.
                                            This limit applies to each amenity type separately (e.g., Pickleball courts, Squash courts).
                                            Members are counted toward this limit whether they are the booking host or a guest on another member's booking.
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Daily Booking Limit (minutes)"
                                            type="number"
                                            value={settings.daily_amenity_booking_limit || 120}
                                            onChange={(e) => updateSetting('daily_amenity_booking_limit', parseInt(e.target.value) || 120)}
                                            disabled={!canEditSettings}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                min: 30,
                                                max: 1440, // Max 24 hours
                                                step: 15 // 15 minute increments
                                            }}
                                            helperText={`${Math.floor((settings.daily_amenity_booking_limit || 120) / 60)} hours ${(settings.daily_amenity_booking_limit || 120) % 60} minutes`}
                                            sx={{ maxWidth: 400 }}
                                        />

                                        <Alert severity="info" sx={{ mt: 3 }}>
                                            <Typography variant="body2">
                                                <strong>How it works:</strong>
                                            </Typography>
                                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                                <li>Each amenity type has a separate daily limit (e.g., 2 hours for Pickleball, 2 hours for Squash)</li>
                                                <li>Members cannot exceed this limit whether booking as host or joining as a guest</li>
                                                <li>This helps ensure fair access to popular amenities</li>
                                                <li>Default is 120 minutes (2 hours) per amenity type per day</li>
                                            </ul>
                                        </Alert>
                                    </Box>
                                )}

                                {/* Prime Time Booking Limit */}
                                {systemTabSelection === 'prime-time-booking-limit' && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Prime Time Booking Limit
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            Set the maximum number of <strong>peak-time</strong> minutes a member can book per amenity type per day.
                                            Only minutes that fall within peak-priced slots (as configured in the amenity schedule) count toward this limit.
                                            Set to 0 to disable the prime time limit.
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Prime Time Booking Limit (minutes)"
                                            type="number"
                                            value={settings.prime_time_booking_limit ?? 0}
                                            onChange={(e) => updateSetting('prime_time_booking_limit', parseInt(e.target.value) || 0)}
                                            disabled={!canEditSettings}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                min: 0,
                                                max: 1440,
                                                step: 15
                                            }}
                                            helperText={
                                                (settings.prime_time_booking_limit ?? 0) === 0
                                                    ? 'Disabled — no prime time limit is enforced'
                                                    : `${Math.floor((settings.prime_time_booking_limit ?? 0) / 60)} hours ${(settings.prime_time_booking_limit ?? 0) % 60} minutes of peak time per amenity type per day`
                                            }
                                            sx={{ maxWidth: 400 }}
                                        />

                                        <Alert severity="info" sx={{ mt: 3 }}>
                                            <Typography variant="body2">
                                                <strong>How it works:</strong>
                                            </Typography>
                                            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                                                <li>Prime time hours are defined as slots with <strong>Peak</strong> pricing tier in the amenity schedule</li>
                                                <li>Only the minutes of a booking that fall within peak-priced schedule slots count toward this limit</li>
                                                <li>The limit applies per amenity type separately and includes time as both host and guest</li>
                                                <li>This is separate from and in addition to the overall daily booking limit</li>
                                                <li>Set to 0 (default) to disable this limit entirely</li>
                                            </ul>
                                        </Alert>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Appearance Settings Tab */}
                <TabPanel value={tabIndex} index={2}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box component={Card} width="100%">
                                <CardHeader title="Visual Appearance" />
                                <CardContent>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Logo Image URL"
                                        value={settings.tenant_logo_img}
                                        onChange={(e) => updateSetting('tenant_logo_img', e.target.value)}
                                        disabled={!canEditSettings}
                                        helperText="URL to your organization's logo image"
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Hero Image URL"
                                        value={settings.tenant_hero_img}
                                        onChange={(e) => updateSetting('tenant_hero_img', e.target.value)}
                                        disabled={!canEditSettings}
                                        helperText="URL to your hero/banner image"
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Background Color"
                                        value={settings.tenant_background_colour}
                                        onChange={(e) => updateSetting('tenant_background_colour', e.target.value)}
                                        disabled={!canEditSettings}
                                        InputProps={{
                                            endAdornment: colorPreview(settings.tenant_background_colour)
                                        }}
                                        helperText="Hex color code for background (e.g., #ffffff for white)"
                                    />

                                    {settings.tenant_logo_img && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>Logo Preview:</Typography>
                                            <img
                                                src={settings.tenant_logo_img}
                                                alt="Logo Preview"
                                                style={{ maxWidth: '100%', maxHeight: '100px' }}
                                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                    e.currentTarget.src = 'https://via.placeholder.com/200x100?text=Invalid+Image+URL';
                                                }}
                                            />
                                        </Box>
                                    )}                {settings.tenant_hero_img && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom>Hero Image Preview:</Typography>
                                            <img
                                                src={settings.tenant_hero_img}
                                                alt="Hero Preview"
                                                style={{ maxWidth: '100%', maxHeight: '150px' }}
                                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                    e.currentTarget.src = 'https://via.placeholder.com/400x150?text=Invalid+Image+URL';
                                                }}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Box>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Receipts Settings Tab */}
                <TabPanel value={tabIndex} index={4}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box component={Card} width="100%">
                                <CardHeader 
                                    title="Receipt & Invoice Settings" 
                                    avatar={<ReceiptIcon />}
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Customize how receipts and invoices appear for your members. These details will be displayed on all transaction receipts and invoices.
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Organization Name"
                                        value={settings.receipt_organization_name}
                                        onChange={(e) => updateSetting('receipt_organization_name', e.target.value)}
                                        disabled={!canEditSettings}
                                        helperText="Your organization's legal or business name"
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Organization Address"
                                        value={settings.receipt_organization_address}
                                        onChange={(e) => updateSetting('receipt_organization_address', e.target.value)}
                                        disabled={!canEditSettings}
                                        multiline
                                        rows={3}
                                        helperText="Complete address including street, city, province/state, postal code"
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="GST/HST Registration Number"
                                        value={settings.receipt_gst_hst_number}
                                        onChange={(e) => updateSetting('receipt_gst_hst_number', e.target.value)}
                                        disabled={!canEditSettings}
                                        helperText="Your tax registration number (if applicable)"
                                    />

                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Preview Information:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            The header of receipts will use the <strong>Background Color</strong> from the Appearance settings.
                                            Member addresses will be included on invoices when available.
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Box>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Save button */}
                {canEditSettings && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={saveSettings}
                            disabled={saving}
                            startIcon={saving && <CircularProgress size={20} color="inherit" />}
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </Box>
                )}

                {/* Footer with version info icon */}
                <Box sx={{ position: 'fixed', bottom: 16, right: 24, zIndex: 9999 }}>
                    <IconButton color="primary" onClick={() => setShowVersion(true)}>
                        <InfoOutlinedIcon />
                    </IconButton>
                    <Snackbar
                        open={showVersion}
                        autoHideDuration={4000}
                        onClose={() => setShowVersion(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        sx={{ minWidth: 200 }}
                    >
                        <Alert onClose={() => setShowVersion(false)} severity="info" sx={{ width: '100%' }}>
                            Version: {import.meta.env.VITE_REACT_APP_VERSION}
                        </Alert>
                    </Snackbar>
                </Box>

                {/* PT Type Dialog */}
                <Dialog
                    open={ptTypeDialogOpen}
                    onClose={() => {
                        setPTTypeDialogOpen(false);
                        setEditingPTType(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingPTType ? 'Edit PT Type' : 'Add PT Type'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Type Title"
                            fullWidth
                            defaultValue={editingPTType?.title || ''}
                            id="pt-type-title"
                            helperText="e.g., Private Session, Semi-Private, Small Group"
                        />
                        <TextField
                            margin="dense"
                            label="Default Price"
                            type="number"
                            fullWidth
                            defaultValue={editingPTType?.defaultPrice || 0}
                            id="pt-type-price"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0, step: 0.01 }
                            }}
                            helperText="Default price for this type (can be overridden per session)"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setPTTypeDialogOpen(false);
                            setEditingPTType(null);
                        }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                const titleInput = document.getElementById('pt-type-title') as HTMLInputElement;
                                const priceInput = document.getElementById('pt-type-price') as HTMLInputElement;

                                const title = titleInput?.value?.trim();
                                const defaultPrice = parseFloat(priceInput?.value || '0');

                                if (title) {
                                    savePTType({ title, defaultPrice });
                                }
                            }}
                            variant="contained"
                        >
                            {editingPTType ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </AdminAccessControl>
        </Container>
    );
};

export default SettingsPage;
